import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { scrollToElement } from '../../utils/helpers';

interface NavItem {
  label: string;
  href: string;
  isExternal?: boolean;
}

interface HeaderProps {
  transparent?: boolean;
}

const Header: React.FC<HeaderProps> = ({ transparent = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const storedUser = localStorage.getItem('adminUser');
  const loggedInUser = storedUser ? JSON.parse(storedUser) : null;
  const isAuthorizedStaff = loggedInUser && ['deo', 'accounts', 'admin', 'super_admin'].includes(loggedInUser.role);

  const navItems: NavItem[] = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Vision', href: '#vision' },
    { label: 'Kreed Sahayak', href: '/class', isExternal: true },
    ...(isAuthorizedStaff ? [{ label: 'Register', href: '/apply', isExternal: true }] : []),
    { label: 'Check Status', href: '/check-status', isExternal: true },
    { label: 'Contact', href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, isExternal?: boolean) => {
    setIsMenuOpen(false);
    
    if (!isExternal && href.startsWith('#')) {
      const elementId = href.substring(1);
      if (location.pathname === '/') {
        scrollToElement(elementId);
      } else {
        navigate('/' + href);
      }
    }
  };

  const shouldShowScrolled = isScrolled || !transparent;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        shouldShowScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between h-20 sm:h-24">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              className="flex items-center space-x-3"
              onClick={() => scrollToElement('home')}
            >
              <img 
                src="/logo192.png" 
                alt="Kreed Odisha Logo" 
                className="w-16 h-16 object-contain"
              />
              <div className="flex flex-col">
                <span className={`font-bold text-2xl transition-colors ${
                  shouldShowScrolled ? 'text-gray-900' : 'text-white'
                }`}>
                  Kreed Odisha
                </span>
                <span className={`text-base transition-colors ${
                  shouldShowScrolled ? 'text-gray-600' : 'text-gray-200'
                }`}>
                  The Pathway to Sporting Excellence
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navItems.map((item) => (
                item.isExternal ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 hover:bg-primary-600 hover:text-white ${
                      shouldShowScrolled 
                        ? 'text-gray-700 hover:text-white' 
                        : 'text-white hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href, item.isExternal)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 hover:bg-primary-600 hover:text-white ${
                      shouldShowScrolled 
                        ? 'text-gray-700 hover:text-white' 
                        : 'text-white hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Apply Now Button (Desktop) */}
          {isAuthorizedStaff && (
            <div className="hidden md:block">
              <Link
                to="/apply"
                className="btn-primary"
              >
                Register Now
              </Link>
            </div>
          )}

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md transition-colors ${
                shouldShowScrolled 
                  ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100' 
                  : 'text-white hover:text-gray-200 hover:bg-white/10'
              }`}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-lg">
              {navItems.map((item) => (
                item.isExternal ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-gray-700 hover:bg-primary-600 hover:text-white block px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href, item.isExternal)}
                    className="text-gray-700 hover:bg-primary-600 hover:text-white block px-3 py-2 rounded-md text-base font-medium transition-colors w-full text-left"
                  >
                    {item.label}
                  </button>
                )
              ))}
              {isAuthorizedStaff && (
                <div className="pt-2">
                  <Link
                    to="/apply"
                    className="btn-primary w-full text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Register Now
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;